import { useEffect, useState } from "react"
import { ResourceFile, ResourceProps } from "../Interfaces"
import { get_resources_by_type } from "../API"
import { ResourceListContainer } from "./ResourceListContainer"
import { Toaster } from "sonner"

export function BaseResourceTemplate({ type, title, description, icon: Icon }: ResourceProps) {
    const [resources, setResources] = useState<ResourceFile[]>([])

    const fetchResources = async () => {
        const resources = await get_resources_by_type(type)
        setResources(resources)
    }

    useEffect(() => {
        fetchResources()
    }, [type])

    const handleResourceRefresh = () => {
        fetchResources() // Refresh the resource list after deletion
    }

    return (
        <PageContainer>
            <HeroSection title={title} description={description} icon={Icon} />
            <MainContent>
                <ResourceListContainer 
                    resources={resources} 
                    type={type} 
                    onResourceRefresh={handleResourceRefresh}
                />
            </MainContent>
            <Toaster />
        </PageContainer>
    )
}

// Page-level container with background gradient
function PageContainer({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 to-orange-50">
            {children}
        </div>
    )
}

// Hero section with title, description, and status indicators
function HeroSection({ title, description, icon: Icon }: { 
    title: string; 
    description: string; 
    icon: React.ComponentType<{ className?: string }>;
}) {
    return (
        <div className="relative bg-white shadow-sm border-b border-gray-200">
            <HeroBackground />
            <HeroContent>
                <HeroIconContainer>
                    <Icon className="w-10 h-10 text-white" />
                </HeroIconContainer>
                <HeroTextSection title={title} description={description} />
            </HeroContent>
        </div>
    )
}

// Background gradient overlay for hero section
function HeroBackground() {
    return (
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-amber-500/5" />
    )
}

// Content wrapper for hero section
function HeroContent({ children }: { children: React.ReactNode }) {
    return (
        <div className="relative max-w-7xl mx-auto px-6 py-12">
            <div className="flex items-center justify-center space-x-6">
                {children}
            </div>
        </div>
    )
}

// Icon container with orange gradient background
function HeroIconContainer({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-lg">
            {children}
        </div>
    )
}

// Title, description, and status indicators section
function HeroTextSection({ title, description }: { title: string; description: string }) {
    return (
        <div className="text-center">
            <HeroTitle title={title} />
            <HeroDescription description={description} />
            <StatusIndicators />
        </div>
    )
}

// Main page title
function HeroTitle({ title }: { title: string }) {
    return (
        <h1 className="text-4xl font-bold text-gray-900 mb-2 tracking-tight">
            {title} Resources
        </h1>
    )
}

// Page description
function HeroDescription({ description }: { description: string }) {
    return (
        <p className="text-lg text-gray-600 font-medium">
            {description}
        </p>
    )
}

// Status indicators (Cloud storage, Secure access)
function StatusIndicators() {
    return (
        <div className="flex items-center justify-center mt-4 space-x-4 text-sm text-gray-500">
            <StatusIndicator color="bg-orange-400" text="Cloud storage" />
            <StatusDivider />
            <StatusIndicator color="bg-amber-400" text="Secure access" />
        </div>
    )
}

// Individual status indicator with colored dot
function StatusIndicator({ color, text }: { color: string; text: string }) {
    return (
        <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 ${color} rounded-full`}></div>
            <span>{text}</span>
        </div>
    )
}

// Small divider dot between status indicators
function StatusDivider() {
    return <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
}

// Main content area container
function MainContent({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex-1 flex flex-col">
            {children}
        </div>
    )
}